import {
  ENDPOINTS,
  ManagePartyAliasRequestSchema,
  ResolvePartyRequestSchema,
  type ManagePartyAliasRequest,
  type ManagePartyAliasResponse,
  type ResolvePartyRequest,
  type ResolvePartyResponse,
} from '@clm/shared-types';

import { apiGet, apiPost, type Query } from './client';

/**
 * `resolve-party` and `manage-party-alias` — the two calls behind the Party
 * aliases surface (CLM-56, D-B8).
 *
 * The party-alias list **is** the FR-29 control: substitution is string
 * replacement of known values, so what a human approves through
 * `managePartyAlias` is exactly what leaves the tenant tokenised on the next
 * pseudonymisation run. That is why every write here is one action, validated
 * against the contract before it is sent — a `reject` without a `reason` throws
 * a `ZodError` in this module and never reaches `apiPost`.
 *
 * All query parameters on the read, same encoding as the verification queue:
 * dotted keys for the nested page object, repeated keys for arrays (never a
 * comma-join), literal `true`/`false` for booleans
 * (`apps/api/src/http/decode.ts`).
 *
 * Copy in the surface says **"party alias"** everywhere. "Alias" alone clashes
 * with the clause-search aliases of Phase 5, which are a different table with
 * different semantics.
 */
const RESOLVE_ENDPOINT = ENDPOINTS['resolve-party'];
const MANAGE_ENDPOINT = ENDPOINTS['manage-party-alias'];

export function resolvePartyQuery(request: ResolvePartyRequest): Query {
  const query: Record<string, string | number | boolean | readonly string[]> = {};

  if (request.party_key !== undefined) query['party_key'] = request.party_key;
  if (request.search !== undefined) query['search'] = request.search;
  if (request.party_type !== undefined) query['party_type'] = [...request.party_type];
  if (request.include_inactive !== undefined) query['include_inactive'] = request.include_inactive;
  if (request.include_aliases !== undefined) query['include_aliases'] = request.include_aliases;
  if (request.include_hierarchy !== undefined) query['include_hierarchy'] = request.include_hierarchy;
  if (request.alias_state !== undefined) query['alias_state'] = [...request.alias_state];
  if (request.require_alias_match !== undefined) {
    query['require_alias_match'] = request.require_alias_match;
  }
  /* CLM-114 (R-114-5) — each party's vendor-list references, for the one-party view's "Vendor list" panel. */
  if (request.include_references !== undefined) query['include_references'] = request.include_references;
  if (request.page?.limit !== undefined) query['page.limit'] = request.page.limit;
  if (request.page?.cursor !== undefined) query['page.cursor'] = request.page.cursor;

  return query;
}

export async function fetchResolveParty(
  request: ResolvePartyRequest,
  signal?: AbortSignal,
): Promise<ResolvePartyResponse> {
  const validated = ResolvePartyRequestSchema.parse(request);
  const raw = await apiGet<unknown>(RESOLVE_ENDPOINT.path, {
    query: resolvePartyQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return RESOLVE_ENDPOINT.response.parse(raw);
}

/**
 * One party-alias action. The body is the action — no envelope — exactly as
 * validated; the server's own schema is the same object, so a shape this
 * function accepts is a shape the gateway accepts.
 */
export async function managePartyAlias(
  request: ManagePartyAliasRequest,
  signal?: AbortSignal,
): Promise<ManagePartyAliasResponse> {
  const body = ManagePartyAliasRequestSchema.parse(request);
  const raw = await apiPost<unknown>(MANAGE_ENDPOINT.path, body, signal === undefined ? {} : { signal });
  return MANAGE_ENDPOINT.response.parse(raw);
}
